// stores/vehicles.js
import { defineStore } from "pinia";
import { ref } from "vue";
import axios from "axios";

export const useVehicleStore = defineStore("vehicles", () => {
  const vehicles = ref([]);
  const isLoading = ref(false);

  const fetchVehicles = async () => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      const response = await axios.get("/api/vehicles");
      vehicles.value = response.data.data;
      return response.data.data;
    } catch (err) {
      throw err.response?.data?.message || "Failed to fetch vehicles";
    } finally {
      isLoading.value = false;
    }
  };

  const fetchVehicleById = async (id) => {
    try {
      const response = await axios.get(`/api/vehicles/${id}`);
      return response.data.data;
    } catch (err) {
      throw err.response?.data?.message || "Failed to get vehicle";
    } finally {
      isLoading.value = false;
    }
  };

  const addVehicle = async (vehicleData) => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      const response = await axios.post("/api/vehicles", vehicleData);
      vehicles.value.push(response.data.data);
      return response.data.data;
    } catch (err) {
      throw err.response?.data?.message || "Failed to add vehicle";
    } finally {
      isLoading.value = false;
    }
  };

  const updateVehicle = async (id, vehicleData) => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      const response = await axios.put(`/api/vehicles/${id}`, vehicleData);
      const index = vehicles.value.findIndex((vehicle) => vehicle.id === id);
      if (index !== -1) {
        vehicles.value[index] = response.data.data;
      }
      return response.data.data;
    } catch (err) {
      throw err.response?.data?.message || "Failed to update vehicle";
    } finally {
      isLoading.value = false;
    }
  };

  const deleteVehicle = async (id) => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      await axios.delete(`/api/vehicles/${id}`);
      vehicles.value = vehicles.value.filter((vehicle) => vehicle.id !== id);
      return true;
    } catch (err) {
      throw err.response?.data?.message || "Failed to delete vehicle";
    } finally {
      isLoading.value = false;
    }
  };

  // Availability check method
  const checkVehicleAvailability = async (params) => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      const response = await axios.get("/api/vehicles/check-availability", {
        params: {
          date: params.date,
          passengersRegularCount: params.passengersRegularCount,
          passengersDisabledCount: params.passengersDisabledCount,
        },
      });
      return response.data.data;
    } catch (err) {
      throw (
        err.response?.data?.message || "Failed to check vehicle availability"
      );
    } finally {
      isLoading.value = false;
    }
  };

  return {
    vehicles,
    isLoading,
    fetchVehicles,
    fetchVehicleById,
    addVehicle,
    updateVehicle,
    deleteVehicle,
    checkVehicleAvailability,
  };
});
